import { zodResolver } from "@hookform/resolvers/zod";
import { CirclePlus } from "lucide-react";
import { observer } from "mobx-react-lite";
import {
  type FC,
  type ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { UseFormReturn } from "react-hook-form";
import { FormProvider, useForm } from "react-hook-form";
import { z } from "zod";

import { CustomButton, DialogBody } from "@/components/atoms";
import { InputControl, toastError, toastSuccess } from "@/components/molecules";
import { Dialog, DialogClose, DialogTrigger } from "@/components/ui/dialog";
import { rootStore } from "@/store/RootStore";
import { commonIconProps20 } from "@/utils/constants";
import { ARABIC_TEXT_WITH_NUMBERS_REGEX } from "@/utils/constants/arabicRegex";

import { useMenuActions } from "../menuActions/MenuActionsContext";
import type { BrandFormMenuType } from "../types";

interface DialogCategoryProps {
  form: UseFormReturn<BrandFormMenuType>;
  nameSelect: "categories";
  disabled: boolean;
  trigger?: ReactNode;
  editId?: string;
  initialNameEn?: string;
  initialNameAr?: string;
}

const DEFAULT_VALUES = { nameEn: "", nameAr: "" };

const DialogCategory: FC<DialogCategoryProps> = observer(
  ({
    form: parentForm,
    disabled,
    trigger,
    editId,
    initialNameEn = "",
    initialNameAr = "",
  }) => {
    const [open, setOpen] = useState(false);
    const skipDuplicateRevalidationRef = useRef(false);
    const isEditing = !!editId;

    const menuCategories = rootStore.menuStore.menu.categories ?? [];

    const existingNames = useMemo(() => {
      return menuCategories
        .filter(c => c.id !== editId)
        .map(c => c.categoryNameEn.toLowerCase());
    }, [menuCategories, editId]);

    const CategoryFormSchema = useMemo(
      () =>
        z.object({
          nameEn: z
            .string()
            .min(1, "Name is required.")
            .max(60, "Name must not exceed 60 characters.")
            .trim()
            // eslint-disable-next-line no-control-regex
            .refine(value => /^[\u0000-\u007F]+$/.test(value), {
              message: "Only English letters, numbers and symbols are allowed.",
            })
            .refine(val => !existingNames.includes(val.toLowerCase()), {
              message: "A category with this name already exists.",
            }),
          nameAr: z
            .string()
            .max(60, "Name must not exceed 60 characters.")
            .regex(ARABIC_TEXT_WITH_NUMBERS_REGEX, {
              message: "Only Arabic characters and numbers are allowed.",
            })
            .trim()
            .or(z.literal(""))
            .transform(v => (v === "" ? undefined : v))
            .optional(),
        }),
      [existingNames]
    );

    type CategoryFormType = z.infer<typeof CategoryFormSchema>;

    const innerForm = useForm<CategoryFormType>({
      resolver: zodResolver(CategoryFormSchema),
      defaultValues: { nameEn: initialNameEn, nameAr: initialNameAr },
      mode: "all",
    });

    const {
      control,
      reset,
      formState: { isValid },
    } = innerForm;

    useEffect(() => {
      if (open) {
        reset(
          isEditing
            ? { nameEn: initialNameEn, nameAr: initialNameAr }
            : DEFAULT_VALUES,
          { keepErrors: false }
        );
      }
    }, [open, isEditing, initialNameEn, initialNameAr, reset]);

    useEffect(() => {
      if (!open || skipDuplicateRevalidationRef.current) return;

      const nameEn = innerForm.getValues("nameEn").trim();
      if (!nameEn) return;

      innerForm.trigger("nameEn");
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [existingNames]);

    const menuActions = useMenuActions();

    const handleSave = async () => {
      const data = innerForm.getValues();
      const result = CategoryFormSchema.safeParse(data);

      if (!result.success) return;

      const menuStore = rootStore.menuStore;
      skipDuplicateRevalidationRef.current = true;

      try {
        if (isEditing && editId) {
          await menuActions.updateCategory(
            editId,
            data.nameEn.trim(),
            data.nameAr
          );
          await menuActions.invalidateMenuQuery();
          menuStore.clearCategoryDuplicate(editId);
        } else if (menuActions.isEnabled) {
          const category = await menuActions.createCategory(
            data.nameEn.trim(),
            data.nameAr
          );
          await menuActions.invalidateMenuQuery();
          if (!category) {
            menuStore.addCategory(data.nameEn.trim(), data.nameAr);
          }
        } else {
          menuStore.addCategory(data.nameEn.trim(), data.nameAr);
          toastSuccess("Category added successfully");
        }

        reset(DEFAULT_VALUES);
        setOpen(false);
        parentForm.reset(menuStore.menu);
      } catch (error) {
        toastError(
          error instanceof Error ? error.message : "Failed to save category",
          () => handleSave(),
          "Error"
        );
      } finally {
        skipDuplicateRevalidationRef.current = false;
      }
    };

    return (
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          {trigger || (
            <CustomButton
              className="w-fit [&_svg]:w-6"
              type="button"
              disabled={disabled}
              data-testid="dialog-category-trigger"
            >
              <CirclePlus {...commonIconProps20} />
              Add category
            </CustomButton>
          )}
        </DialogTrigger>

        <DialogBody title={isEditing ? "Edit category" : "New category"}>
          <FormProvider {...innerForm}>
            <form
              onSubmit={e => e.preventDefault()}
              className="flex flex-col pt-2"
            >
              <InputControl<CategoryFormType>
                formControl={control}
                label="Category name (English)"
                nameField="nameEn"
                placeholder="e.g. Starters"
                data-testid="dialog-category-name-en"
              />
              <InputControl<CategoryFormType>
                formControl={control}
                label="Category name (Arabic)"
                nameField="nameAr"
                placeholder="مثال: المقبلات"
                data-testid="dialog-category-name-ar"
              />

              <div className="w-full flex gap-4 mt-4">
                <DialogClose asChild>
                  <CustomButton
                    variant="light"
                    type="button"
                    className="flex-1"
                    data-testid="dialog-category-cancel-button"
                  >
                    Cancel
                  </CustomButton>
                </DialogClose>

                <CustomButton
                  type="button"
                  onClick={() => {
                    handleSave();
                  }}
                  className="flex-1"
                  disabled={!isValid}
                  data-testid="dialog-category-save-button"
                >
                  Save
                </CustomButton>
              </div>
            </form>
          </FormProvider>
        </DialogBody>
      </Dialog>
    );
  }
);

export default DialogCategory;
